import { EkranBledu, NAZWY_W_ZDANIU, TRESCI_EKRANU_BLEDU, nadtytulOrganu } from '@insolitum/ui';
import React from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { ukryjSprzedazSamoobslugowa } from '../lib/fazaSprzedazy';
import { supabase } from '../lib/supabase';
import { getModuleDef } from '../modules/registry';
import { useAuth } from '../providers/AuthProvider';

import { isTrustedModuleOrigin, resolveModuleOrigin } from './moduleOrigin';

/**
 * Nazwa organu do zdania („Agenda chwilowo nie odpowiada", „Organ Agenda").
 *
 * Rejestr powłoki niesie nazwę handlową wersalikami („INSOLITUM AGENDA") —
 * ta stoi w kaflu paska bocznego, ale wpisana w zdanie krojem interfejsu
 * jest fałszywym napisem obok prawdziwego znaku w podpisie. Organ platformy
 * bierze więc nazwę z tej samej mapy, co bramka organu i AuthGuardy
 * (`NAZWY_W_ZDANIU` z `@insolitum/ui`); moduł spoza platformy (partnerski,
 * z bazy) — swoją nazwę z wiersza `modules`.
 */
export function nazwaOrganuWZdaniu(moduleId: string | undefined, nazwa: string): string {
  return (moduleId && NAZWY_W_ZDANIU[moduleId]) || nazwa;
}

/**
 * Organ wyłączony dla organizacji — ta sama odmowa co w bramce organu
 * (`EkranBramkiOrganu`, stan `brak-dostepu`): `EkranBledu` w wariancie
 * `brak-dostepu`, nadtytuł „Organ <nazwa>", bez postaci. „Kafel to organ,
 * goły znak to Insolitum": postacią odmowy organu jest jego kafel, a powłoka
 * kafla 64 px nie ma — więc, jak bramka bez kafla, tekst stoi od nadtytułu,
 * a znak Insolitum zostaje w podpisie. Układ `pelny`: trasy organów
 * (`/admin/modules/<organ>`, kiosk) nie mają nad sobą paska powłoki.
 */
export const ModuleDisabledFallback = ({ name, moduleId }: { name: string; moduleId: string }) => {
  const navigate = useNavigate();
  const { user, currentOrganization } = useAuth();
  // Faza 1 soft startu: kolejny organ uruchamia opiekun ofertą — przycisk do
  // samoobsługowego katalogu odesłałby na `/admin` (strażnik tras).
  const bezKatalogu = ukryjSprzedazSamoobslugowa(currentOrganization?.organization, user);

  return (
    <EkranBledu
      wariant="brak-dostepu"
      uklad="pelny"
      ilustracja={null}
      nadtytul={nadtytulOrganu(nazwaOrganuWZdaniu(moduleId, name))}
      // Pod nadtytułem „Organ <nazwa>” tytuł i treść mówią „organ”, jak
      // odmowy bramki organu w aplikacjach („Organ nie jest aktywny”) —
      // „Organ Agenda / Ten moduł…” nazywało jedną rzecz dwoma słowami
      // w dwóch wierszach. Nazwa MIEJSCA zostaje taka, jak w nawigacji
      // powłoki („Moduły”), bo tam człowiek ją znajdzie.
      tytul="Ten organ został wyłączony dla Twojej organizacji"
      tresc={
        <p>
          {bezKatalogu
            ? 'Aby go uruchomić, skontaktuj się ze swoim opiekunem — przygotuje ofertę.'
            : 'Aby go ponownie włączyć, przejdź do modułów.'}
        </p>
      }
      akcje={
        // Bez katalogu nie ma dokąd odesłać po organ, ale ekran nie może być
        // ślepą uliczką: trasa organu nie ma nad sobą paska powłoki, więc bez
        // akcji jedynym wyjściem byłby wstecz przeglądarki.
        bezKatalogu ? (
          <Link to="/admin" className="btn btn--primary btn--lg">
            Przejdź do pulpitu
          </Link>
        ) : (
          <button
            type="button"
            onClick={() => navigate('/admin/modules')}
            className="btn btn--primary btn--lg"
          >
            Przejdź do modułów
          </button>
        )
      }
    />
  );
};

/**
 * Nie udało się sprawdzić dostępu do organu — ten sam stan co `blad` bramki
 * organu (`EkranBramkiOrganu`): organy odcięte od sieci (`polaczenie`),
 * a nie odmowa. „Nie wiemy" nie jest „nie masz" — zlanie tych dwóch zdań
 * uczy człowieka, że napis „moduł wyłączony" bywa nieprawdą.
 *
 * Obok ponowienia wyjście na pulpit, jak na ekranach awarii UCS: trasa
 * organu nie ma nad sobą paska powłoki, więc przy dłuższej awarii jedynym
 * wyjściem byłby wstecz przeglądarki. `pulpit` podaje strażnik — `/admin`,
 * gdy organizacja jest znana, `/`, gdy nie przyszła nawet lista organizacji.
 */
function EkranBleduDostepu({
  nazwa,
  pulpit,
  onPonow,
}: {
  nazwa: string;
  pulpit: '/admin' | '/';
  onPonow: () => void;
}) {
  return (
    <EkranBledu
      wariant="polaczenie"
      uklad="pelny"
      // Nadtytuł wariantu („Brak połączenia") zakładałby przyczynę, której nie
      // znamy — wiemy tylko, że odpowiedź nie przyszła.
      nadtytul="Brak odpowiedzi"
      tytul="Nie udało się sprawdzić dostępu"
      tresc={
        <p>
          Nie dostaliśmy odpowiedzi z serwera, więc nie wiemy, czy masz dostęp do organu {nazwa}. To
          nie znaczy, że go nie masz.
        </p>
      }
      akcje={
        <>
          <button type="button" className="btn btn--primary btn--lg" onClick={onPonow}>
            Spróbuj ponownie
          </button>
          <Link to={pulpit} className="btn btn--secondary btn--lg">
            Przejdź do pulpitu
          </Link>
        </>
      }
    />
  );
}

/**
 * Nie wiadomo, o którą organizację pytać — ta sama odmowa co
 * `brak-organizacji` bramki organu: `brak-dostepu`, nadtytuł „Organ <nazwa>",
 * bez postaci (powłoka nie ma kafla 64 px). Wyjście na pulpit (`/`), bo ten
 * rozstrzyga panel według roli — konto bez organizacji nie ma pulpitu `/admin`.
 */
function EkranBrakuOrganizacji({ nazwa }: { nazwa: string }) {
  return (
    <EkranBledu
      wariant="brak-dostepu"
      uklad="pelny"
      ilustracja={null}
      nadtytul={nadtytulOrganu(nazwa)}
      tytul="Nie wiemy, o którą organizację pytać"
      tresc={
        <p>
          Twoje konto nie jest przypisane do żadnej czynnej organizacji, więc nie ma kogo zapytać o
          organ {nazwa}.
        </p>
      }
      akcje={
        <Link to="/" className="btn btn--primary btn--lg">
          Przejdź do pulpitu
        </Link>
      }
    />
  );
}

// Module Access Guard - checks if module is enabled
interface ModuleAccessGuardProps {
  moduleId: string;
  moduleName: string;
  children: React.ReactNode;
  /**
   * Dokąd odesłać bez sesji. Domyślnie `/login`, jak `ProtectedRoute`. Trasa
   * kiosku podaje parowanie terminala (`/kiosk/lexshift/activate`): terminal
   * na hali nie ma konta, paruje się kodem — formularz administratora byłby
   * tam ślepą uliczką.
   */
  celBezSesji?: string;
}

/** Odpowiedź bazy na JEDNO pytanie (organizacja × organ × próba). */
interface OdpowiedzDostepu {
  pytanie: string;
  stan: 'ma-dostep' | 'brak-dostepu' | 'blad';
}

/**
 * Strażnik wejścia do organu w powłoce. Tak jak bramka organu
 * (`EkranBramkiOrganu`) rozróżnia „nie wiemy" od „nie masz" — do 05.10.2026
 * błąd `ma_dostep` i brak organizacji kończyły się tym samym zdaniem „Ten
 * moduł został wyłączony dla Twojej organizacji", czyli nieprawdą:
 *
 * | sytuacja                                    | ekran                                |
 * | ------------------------------------------- | ------------------------------------ |
 * | brak sesji                                  | `/login`, jak `ProtectedRoute`       |
 * | brak sesji na trasie kiosku (bez strażnika) | `celBezSesji` — parowanie terminala  |
 * | organizacje jeszcze się wczytują            | szkielet ładowania                   |
 * | lista organizacji nie przyszła (`error`)    | `blad` — ponowienie odświeża listę   |
 * | konto bez organizacji                       | `brak-organizacji` (odmowa)          |
 * | `ma_dostep` z błędem albo wyjątkiem         | `blad` — ponowienie pyta jeszcze raz |
 * | `ma_dostep` = false                         | `ModuleDisabledFallback`             |
 * | odpowiedź na pytanie już nie bieżące        | odrzucona (sprzątanie efektu)        |
 *
 * Bramka: `tests/unit/apps/sygnalZyciaOrganu.test.tsx`.
 */
export const ModuleAccessGuard: React.FC<ModuleAccessGuardProps> = ({
  moduleId,
  moduleName,
  children,
  celBezSesji = '/login',
}) => {
  const {
    currentOrganization,
    isLoading: authLoading,
    isAuthenticated,
    organizationsStatus,
    refreshUserData,
  } = useAuth();
  const location = useLocation();
  const organizationId = currentOrganization?.organization_id ?? null;
  const [proba, setProba] = React.useState(0);
  // Odpowiedź jest przypięta do pytania: po zmianie organizacji stara
  // odpowiedź nie wpuszcza do organu nawet na jedną klatkę.
  const pytanie = `${organizationId}|${moduleId}|${proba}`;
  const [odpowiedz, setOdpowiedz] = React.useState<OdpowiedzDostepu | null>(null);
  const stan = odpowiedz?.pytanie === pytanie ? odpowiedz.stan : 'sprawdzam';

  React.useEffect(() => {
    if (authLoading || !organizationId) return;
    // Odpowiedź na pytanie, które przestało być bieżące, nie zapisuje się
    // wcale. Samo przypięcie do pytania (wyżej) nie wystarcza: spóźniona
    // odpowiedź dla poprzedniej organizacji nadpisywała odpowiedź bieżącą,
    // stan wracał do `sprawdzam`, a zależności efektu się nie zmieniały — nikt
    // nie pytał ponownie i działający organ gasł na szkielecie bez końca.
    let aktualne = true;

    const checkModuleAccess = async () => {
      try {
        // O prawo wejścia pyta się BAZY, a nie składa się go z zapytania po
        // `module_subscriptions` plus warunków na kliencie. Reguła (statusy,
        // wygaśnięcie trialu, koniec subskrypcji) i zakres pytającego mieszkają
        // od migracji 674 w `moduly_z_dostepem(uuid)`, a `ma_dostep` do niej
        // deleguje. Poprzednia wersja tego bloku nie znała
        // `subscription_ends_at` — pola, którym `module_subscription_set_enabled`
        // (467:227) gasi moduł — więc wyłączony moduł zostawał na ekranie.
        const { data, error } = await supabase.rpc('ma_dostep', {
          p_organization_id: organizationId,
          p_module_id: moduleId,
        });

        if (!aktualne) return;
        if (error) {
          console.error('[ModuleAccessGuard] ma_dostep nie odpowiedziało:', error.message);
          setOdpowiedz({ pytanie, stan: 'blad' });
          return;
        }

        setOdpowiedz({ pytanie, stan: data === true ? 'ma-dostep' : 'brak-dostepu' });
      } catch (err) {
        if (!aktualne) return;
        console.error('[ModuleAccessGuard] Error checking module access:', err);
        setOdpowiedz({ pytanie, stan: 'blad' });
      }
    };

    checkModuleAccess();
    return () => {
      aktualne = false;
    };
  }, [moduleId, organizationId, authLoading, pytanie]);

  if (authLoading) {
    return <ModuleLoadingFallback name={moduleName} />;
  }

  if (!isAuthenticated) {
    return <Navigate to={celBezSesji} state={{ from: location }} replace />;
  }

  const nazwa = nazwaOrganuWZdaniu(moduleId, moduleName);

  if (!organizationId) {
    if (organizationsStatus === 'loading') return <ModuleLoadingFallback name={moduleName} />;
    if (organizationsStatus === 'error') {
      // Bez listy organizacji nie wiadomo, czy konto ma pulpit `/admin` —
      // `/` rozstrzyga panel według roli (jak przy koncie bez organizacji).
      return <EkranBleduDostepu nazwa={nazwa} pulpit="/" onPonow={() => void refreshUserData()} />;
    }
    return <EkranBrakuOrganizacji nazwa={nazwa} />;
  }

  if (stan === 'sprawdzam') {
    return <ModuleLoadingFallback name={moduleName} />;
  }

  if (stan === 'blad') {
    return (
      <EkranBleduDostepu nazwa={nazwa} pulpit="/admin" onPonow={() => setProba((p) => p + 1)} />
    );
  }

  if (stan === 'brak-dostepu') {
    return <ModuleDisabledFallback name={moduleName} moduleId={moduleId} />;
  }

  return <>{children}</>;
};

// Loading fallback component — kompozycja szkieletowa (.skel) odwzorowująca
// panel modułu z prototypu FAZY 2: nagłówek (kafel + nazwa), rząd KPI, treść.
//
// Szkielet, a nie znak startu: zaraz po nim organ w ramce rysuje WŁASNY
// `EkranStartowy` (AuthGuard, „Wczytywanie organu Agenda…"). Ramka to inny
// dokument z innego originu — pamięć startu nie przechodzi przez jej granicę —
// więc znak startu powłoki tuż przed znakiem startu organu dałby dwa starty
// z dwoma zegarami animacji, jeden po drugim. Powłoka nie tłumaczy ekranów
// stanu organu (sąsiednie ekrany w tym pliku też mówią wyłącznie po polsku),
// stąd zdanie bez `t()`.
export const ModuleLoadingFallback = ({ name }: { name: string }) => (
  <div className="min-h-screen bg-aurora-bg p-8 space-y-6" aria-busy="true">
    <div className="flex items-center gap-4">
      <div className="w-14 h-14 rounded-ids-lg skel" aria-hidden="true" />
      <div>
        <h2 className="display text-xl font-bold text-aurora-text">{name}</h2>
        <p className="text-sm text-aurora-muted">Wczytywanie…</p>
      </div>
    </div>
    <div className="grid kpis" aria-hidden="true">
      <div className="h-28 skel" />
      <div className="h-28 skel" />
      <div className="h-28 skel" />
      <div className="h-28 skel" />
    </div>
    <div className="h-96 skel" aria-hidden="true" />
  </div>
);

/**
 * Organ w ramce nie odpowiada: znak z jednym organem uśpionym, nazwa organu
 * w tytule („Agenda chwilowo nie odpowiada"). Układ `pelny`, bo ramka organu
 * zajmuje cały ekran — trasa organu nie ma nad sobą paska powłoki. Wyzwala go
 * brak sygnału życia organu (`ModuleFrame`, `ORGANY_Z_SYGNALEM_ZYCIA`).
 *
 * Akcje jak na ekranach awarii UCS: „Spróbuj ponownie” (nowa ramka)
 * i „Przejdź do pulpitu” (`/admin`) — bez paska powłoki to jedyne wyjście
 * poza wstecz przeglądarki. W wierszu akcji stoją najwyżej dwa przyciski,
 * więc wyjście do organu w nowej karcie (ramka bywa zablokowana, organ
 * w karcie nie) jest odnośnikiem w treści: do 05.10.2026 stało w wierszu
 * akcji ZAMIAST pulpitu, a otwierało ten sam niedziałający organ. Bramki:
 * `tests/unit/apps/organNiedostepnyWPowloce.test.tsx` (wygląd),
 * `tests/unit/apps/sygnalZyciaOrganu.test.tsx` (wyzwalacz i fokus).
 */
export function EkranOrganuNiedostepnego({
  organ,
  adres,
  onPonow,
}: {
  /** Nazwa organu do zdania (`nazwaOrganuWZdaniu`). */
  organ: string;
  /** Adres ramki — ten sam, który nie wstał. */
  adres: string;
  onPonow: () => void;
}) {
  const korzen = React.useRef<HTMLElement>(null);
  // Fokus na tytuł przy pojawieniu się — ten sam mechanizm co `PanelBledu`
  // (tytuł `EkranBledu` ma `tabIndex={-1}`). Ekran przychodzi po ciszy, bez
  // żadnego działania człowieka, więc bez tego czytnik ekranu nie wie, że
  // pod nim zmienił się widok.
  React.useEffect(() => {
    korzen.current?.querySelector<HTMLElement>('.ids-ekran-bledu__tytul')?.focus();
  }, []);

  return (
    <EkranBledu
      ref={korzen}
      wariant="organ-niedostepny"
      uklad="pelny"
      organ={organ}
      tresc={
        <p>
          {/* Zdanie domyślne wariantu (bez kropki) + wyjście do karty — jedno
              źródło zdania, nie druga jego kopia. */}
          {TRESCI_EKRANU_BLEDU['organ-niedostepny'].tresc.replace(/\.$/, '')} albo{' '}
          {/* Podkreślenie, nie sam kolor, odróżnia odnośnik od tekstu akapitu
              (WCAG 1.4.1) — preflight Tailwinda zdejmuje je z każdego `<a>`.
              Odnośnik nie łamie się w środku (`whitespace-nowrap`): przy
              kolumnie 480 px wiersz kończył się na „otwórz”, a „organ
              w nowej karcie” schodziło niżej — jedno działanie w dwóch
              kawałkach, z podkreśleniem urwanym na końcu wiersza. */}
          <a
            href={adres}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 whitespace-nowrap"
          >
            otwórz organ w nowej karcie
          </a>
          .
        </p>
      }
      akcje={
        <>
          <button type="button" className="btn btn--primary btn--lg" onClick={onPonow}>
            Spróbuj ponownie
          </button>
          <Link to="/admin" className="btn btn--secondary btn--lg">
            Przejdź do pulpitu
          </Link>
        </>
      }
    />
  );
}

// Iframe-based module component
/**
 * Czy `path` z komunikatu `navigate` wskazuje wnętrze powłoki.
 *
 * Wymagamy pojedynczego wiodącego ukośnika. Odpada przez to `//host` (adres
 * protokołowo-względny — przeglądarka potraktuje go jak zewnętrzną domenę),
 * `https://…`, `javascript:` i wszystko, co nie jest ścieżką aplikacji.
 * Eksportowane, bo to jest reguła, a nie szczegół implementacji — test ma ją
 * z czego sprawdzić.
 */
export function isWewnetrznaSciezka(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//');
}

/**
 * Parametry adresu powłoki, które ramka podaje organowi (Temperatio A+, przegląd 26.10.2026). Dotąd ramka nie
 * przekazywała NICZEGO poza `org_id`, więc „Ustaw w Temperatio” z kartoteki centralnej otwierał listę 489 kart OGYY
 * zamiast karty, od której człowiek przyszedł. Lista jest zamknięta, a wartość musi mieć kształt UUID: adres powłoki
 * da się podać z zewnątrz (odnośnik), a organ dostaje z niego wyłącznie identyfikator, który sam sprawdza na swojej
 * liście zawężonej do organizacji — `org_id` i każdy inny parametr ustawia wyłącznie powłoka.
 */
const PARAMETRY_DLA_ORGANU = ['karta'] as const;
const KSZTALT_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function parametryDlaOrganu(search: string): Array<[string, string]> {
  const zAdresu = new URLSearchParams(search);
  const wynik: Array<[string, string]> = [];
  for (const nazwa of PARAMETRY_DLA_ORGANU) {
    const wartosc = zAdresu.get(nazwa);
    if (wartosc && KSZTALT_UUID.test(wartosc)) wynik.push([nazwa, wartosc]);
  }
  return wynik;
}

// =============================================================================
// SYGNAŁ ŻYCIA ORGANU W RAMCE
//
// Ramka nie umie powiedzieć powłoce, że organ nie wstał. React 18 podpina na
// <iframe> wyłącznie `load`, a Chromium przy odmowie połączenia i złej domenie
// też emituje `load`, nie `error` (zmierzone 05.10.2026) — ramka pokazuje
// wtedy stronę błędu przeglądarki i dla powłoki wygląda na wczytaną.
//
// Dlatego organ ŻYJE dopiero wtedy, gdy SAM się odezwie: pierwszy komunikat
// z originu ramki (`isTrustedModuleOrigin`) o znanym typie. Każdy organ
// z listy niżej wysyła `REQUEST_AUTH_SESSION` przy montażu w ramce
// BEZWARUNKOWO — z pierwszego efektu AuthGuarda, który stoi w korzeniowym
// layoucie i nie czeka na sesję (także gdy organ ma sesję z pamięci i gdy ma
// własny formularz logowania: Aegis, LexShift, Temperatio pokazują go tylko
// POZA ramką). Pilnuje tego `tests/unit/apps/sygnalZyciaWOrganach.test.tsx`,
// renderując AuthGuard każdego organu z listy.
//
// Do sygnału ramka jest ukryta pod szkieletem — także po `load`, bo ten
// przychodzi i dla strony błędu przeglądarki. Brak sygnału przez
// `CZAS_NA_SYGNAL_ZYCIA_MS` od startu ładowania → `EkranOrganuNiedostepnego`
// nad ukrytą ramką, która ładuje się dalej; spóźniony sygnał zdejmuje ekran
// sam i pokazuje ramkę.
// =============================================================================

/** Organy objęte wykrywaniem — każdy sprawdzony w kodzie AuthGuarda. */
export const ORGANY_Z_SYGNALEM_ZYCIA: readonly string[] = [
  'agenda',
  'artifex',
  'conspectus',
  'fabrica',
  'furnus',
  'gamification',
  'lexshift',
  'neurosense',
  'provisio',
  'waterandice',
];

/**
 * Organy, których ramka dostaje Bluetooth (`allow="bluetooth"`). Bez tego
 * Chrome odrzuca `navigator.bluetooth.requestDevice()` w ramce od razu
 * („Access to the feature "bluetooth" is disallowed by permissions policy"),
 * zanim pokaże okno wyboru — zmierzone 28.10.2026 na Androidzie przy
 * parowaniu czujnika Shelly (NeuroSense, plan 61 E1). Lista jest zamknięta:
 * tylko organ, który Bluetooth naprawdę woła. Moduły partnerskie
 * (`DynamicModuleFrame`) go nie dostają.
 */
export const ORGANY_Z_BLUETOOTH: readonly string[] = ['neurosense'];

/** Uprawnienia ramki organu (atrybut `allow`). */
export function uprawnieniaRamki(bluetooth: boolean): string {
  return bluetooth
    ? 'camera; microphone; geolocation; bluetooth'
    : 'camera; microphone; geolocation';
}

/**
 * Organy rejestru poza wykrywaniem — jawnie i z powodem. Moduły partnerskie
 * (`DynamicModuleFrame`) są poza nim z definicji: ich kodu nie mamy, więc nie
 * da się udowodnić, że się odezwą — fałszywy alarm nad działającym modułem
 * byłby gorszy niż brak ekranu.
 */
export const POZA_SYGNALEM_ZYCIA: Readonly<Record<string, string>> = {
  nexus: 'organ natywny powłoki (własne trasy, bez ramki) — nie ma czego wykrywać',
  ucs:
    'ramkę montuje UcsModulePage, a aplikacja `/embedded` (ucs.insolitum.ai) jest spoza ' +
    'tego repozytorium — nie da się sprawdzić, czy przy montażu woła REQUEST_AUTH_SESSION',
};

/**
 * 20 s od startu ładowania. Pierwszy komunikat organu wychodzi ≤100 ms po
 * zamontowaniu AuthGuarda, czyli po hydratacji. Strony organów są
 * prerenderowane (○ w `next build`), więc dokument przychodzi z CDN, a czeka
 * się głównie na skrypty: `/dashboard` Agendy to 264 kB JS po kompresji
 * (`next build`, 05.10.2026) — ok. 2 s przy 1 Mbit/s, ok. 7 s przy 300 kbit/s
 * słabego zasięgu w hali. 20 s mieści start przy ok. 120 kbit/s — a pomyłka
 * w drugą stronę jest tania: spóźniony komunikat zdejmuje ekran sam, ramka
 * ładuje się pod nim dalej.
 */
export const CZAS_NA_SYGNAL_ZYCIA_MS = 20_000;

/** Komunikaty, które organ wysyła powłoce — każdy dowodzi, że organ wstał. */
const KOMUNIKATY_ORGANU: ReadonlySet<string> = new Set(['REQUEST_AUTH_SESSION', 'navigate']);

interface ModuleFrameProps {
  baseUrl: string;
  path?: string;
  name: string;
  /** Identyfikator organu (`modules.id`) — z niego nazwa w zdaniu ekranu awarii. */
  moduleId?: string;
  /**
   * Czy organ melduje życie (`ORGANY_Z_SYGNALEM_ZYCIA`). Z nim ramka jest
   * widoczna dopiero po sygnale; bez niego — po `load` i bez wyzwalacza
   * ekranu awarii (tak montują ją UCS i moduły partnerskie).
   */
  sygnalZycia?: boolean;
  /** Czy ramka dostaje Bluetooth (`ORGANY_Z_BLUETOOTH`); domyślnie nie. */
  bluetooth?: boolean;
}

export const ModuleFrame: React.FC<ModuleFrameProps> = ({
  baseUrl,
  path = '',
  name,
  moduleId,
  sygnalZycia = false,
  bluetooth = false,
}) => {
  const { currentOrganization } = useAuth();
  const organizationId = currentOrganization?.organization_id ?? null;
  const [isLoading, setIsLoading] = React.useState(true);
  const [sygnal, setSygnal] = React.useState<'czekam' | 'zyje' | 'milczy'>('czekam');
  // Klucz ramki: „Spróbuj ponownie" montuje ją od nowa, czyli ładuje moduł
  // jeszcze raz, zamiast pokazywać ten sam nieudany dokument.
  const [proba, setProba] = React.useState(0);
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const ekranCiszy = React.useRef<HTMLDivElement>(null);
  // Fokus był w ekranie ciszy, który zdejmuje spóźniony sygnał — po zdjęciu
  // przechodzi na ramkę, zamiast spaść na `body` razem z odmontowanym ekranem.
  const fokusDoRamki = React.useRef(false);
  const navigate = useNavigate();

  // The exact origin this frame embeds. Derived from the same baseUrl used to
  // build the iframe src, so it is always correct across prod, Vercel previews
  // and localhost dev. Used as the postMessage targetOrigin (never '*') and as
  // the sole trusted source for inbound messages.
  const moduleOrigin = React.useMemo(() => resolveModuleOrigin(baseUrl), [baseUrl]);

  // Build URL with organization_id query parameter
  //
  // Parametr dla organu (`?karta=`) jest JEDNORAZOWY (trzecia runda weryfikacji Temperatio A+, 26.10.2026): ramka
  // przejmuje go do własnego stanu razem z organizacją, w której przyszedł, i zdejmuje z adresu powłoki. Dotąd adres
  // ramki budował się z adresu powłoki przy każdym przeliczeniu, więc F5 w powłoce, ponowne zamontowanie ramki albo
  // zmiana organizacji otwierały znów okno karty, które człowiek zamknął. Stan, nie sam adres: zdjęcie parametru
  // z adresu powłoki nie może zmienić adresu ramki, bo przeładowałoby organ z otwartym oknem.
  const location = useLocation();
  const { search } = location;
  const [doOrganu, setDoOrganu] = React.useState<{
    org: string;
    pary: Array<[string, string]>;
  } | null>(() => {
    const pary = parametryDlaOrganu(search);
    return organizationId && pary.length > 0 ? { org: organizationId, pary } : null;
  });
  React.useEffect(() => {
    const pary = parametryDlaOrganu(search);
    // Organizacja jeszcze nieznana — parametr czeka w adresie; bez niej nie wiadomo, do której należy.
    if (pary.length === 0 || !organizationId) return;
    setDoOrganu((teraz) =>
      teraz?.org === organizationId && JSON.stringify(teraz.pary) === JSON.stringify(pary)
        ? teraz
        : { org: organizationId, pary }
    );
    const reszta = new URLSearchParams(search);
    for (const [nazwa] of pary) reszta.delete(nazwa);
    const zostaje = reszta.toString();
    navigate(
      { pathname: location.pathname, search: zostaje ? `?${zostaje}` : '', hash: location.hash },
      { replace: true, state: location.state }
    );
  }, [search, organizationId, navigate, location.pathname, location.hash, location.state]);
  const iframeUrl = React.useMemo(() => {
    const url = new URL(`${baseUrl}${path}`);
    // Parametr przejęty w innej organizacji nie jedzie do organu po jej zmianie.
    if (doOrganu && doOrganu.org === organizationId) {
      for (const [nazwa, wartosc] of doOrganu.pary) url.searchParams.set(nazwa, wartosc);
    }
    if (organizationId) {
      url.searchParams.set('org_id', organizationId);
    }
    return url.toString();
  }, [baseUrl, path, organizationId, doOrganu]);

  // Send auth session to iframe
  const sendAuthSession = React.useCallback(async () => {
    if (!iframeRef.current?.contentWindow) {
      console.log('[ModuleFrame] No iframe contentWindow available');
      return;
    }

    // Fail closed: never broadcast session tokens if we cannot pin the exact
    // target origin (a malformed baseUrl / deployUrl would otherwise force '*').
    if (!moduleOrigin) {
      console.warn('[ModuleFrame] Refusing to send auth session: unresolved module origin');
      return;
    }

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session) {
        // Post ONLY to the module's exact origin so the browser drops the
        // message if the iframe was navigated/redirected to another origin.
        console.log('[ModuleFrame] Sending auth session to iframe');
        iframeRef.current.contentWindow.postMessage(
          {
            type: 'AUTH_SESSION',
            payload: {
              access_token: session.access_token,
              refresh_token: session.refresh_token,
            },
          },
          moduleOrigin
        );
      } else {
        console.log('[ModuleFrame] No session available to send');
      }
    } catch (error) {
      console.error('[ModuleFrame] Failed to send auth session to iframe:', error);
    }
  }, [moduleOrigin]);

  // Handle iframe load - send auth session
  const handleIframeLoad = React.useCallback(() => {
    setIsLoading(false);
    // Send auth session after iframe loads
    sendAuthSession();
  }, [sendAuthSession]);

  // Listen for auth state changes and re-send to iframe
  React.useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        sendAuthSession();
      }
    });

    return () => subscription.unsubscribe();
  }, [sendAuthSession]);

  // Listen for navigation messages from iframe
  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Trust ONLY the exact origin this frame embeds. Substring/prefix matches
      // (e.g. origin.includes('insolitum.ai')) are exploitable — see moduleOrigin.ts.
      if (!isTrustedModuleOrigin(event.origin, moduleOrigin)) {
        return;
      }

      // Sygnał życia: organ się odezwał, więc wstał — także spóźniony. Liczy
      // się wyłącznie komunikat z OBECNEJ ramki: „Spróbuj ponownie" montuje
      // nową (`key={proba}`), a stary dokument może jeszcze dostarczyć
      // komunikat z kolejki — origin ten sam, więc sam origin oznaczyłby nową
      // ramkę jako żywą, zanim cokolwiek w niej wstało.
      if (
        typeof event.data?.type === 'string' &&
        KOMUNIKATY_ORGANU.has(event.data.type) &&
        event.source === iframeRef.current?.contentWindow
      ) {
        if (ekranCiszy.current?.contains(document.activeElement)) fokusDoRamki.current = true;
        setSygnal('zyje');
      }

      if (event.data?.type === 'navigate') {
        const targetPath = event.data.path;
        // Ścieżka musi być WEWNĘTRZNA. Dotąd przechodził tu dowolny string, bo
        // moduły wysyłały wyłącznie stałe w rodzaju '/admin/workers'. Odkąd
        // wysyłają adresy budowane z danych (`/admin/workers/<id>`,
        // `/admin/workers?new=1&module=…`), brak walidacji przestaje być
        // teoretyczny: wartość protokołowo-względna ('//gdzies.example')
        // wyprowadziłaby użytkownika z aplikacji jednym postMessage.
        // Sprawdzenie originu wyżej jest poprawne, ale pilnuje NADAWCY, nie
        // CELU — to są dwie różne rzeczy.
        if (typeof targetPath === 'string' && isWewnetrznaSciezka(targetPath)) {
          navigate(targetPath);
        }
      }

      // Handle auth session request from iframe
      if (event.data?.type === 'REQUEST_AUTH_SESSION') {
        console.log('[ModuleFrame] Received REQUEST_AUTH_SESSION from iframe');
        sendAuthSession();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [navigate, sendAuthSession, moduleOrigin]);

  // Zegar sygnału życia: biegnie od startu ładowania KAŻDEGO dokumentu ramki —
  // nowej próby i nowego adresu (zmiana podstrony albo organizacji ładuje
  // organ od nowa, więc ten znowu musi się odezwać).
  React.useEffect(() => {
    if (!sygnalZycia) return;
    setSygnal('czekam');
    const zegar = setTimeout(
      () => setSygnal((s) => (s === 'zyje' ? s : 'milczy')),
      CZAS_NA_SYGNAL_ZYCIA_MS
    );
    return () => clearTimeout(zegar);
  }, [sygnalZycia, proba, iframeUrl]);

  React.useEffect(() => {
    if (sygnal === 'zyje' && fokusDoRamki.current) {
      fokusDoRamki.current = false;
      iframeRef.current?.focus();
    }
  }, [sygnal]);

  const milczy = sygnalZycia && sygnal === 'milczy';
  // Organ z sygnałem życia jest widoczny WYŁĄCZNIE po sygnale — nie po `load`
  // (Chromium wysyła go także dla własnej strony błędu) i bez czekania na
  // `load`, gdy organ odezwał się przed nim. Cena: w normalnym starcie sygnał
  // przychodzi ≤100 ms po zamontowaniu AuthGuarda, a `load` poprzedza
  // hydratację — razem organ pokazuje się 23–46 ms później niż po `load`
  // (zmierzone na prawdziwej Agendzie w ramce cross-site; do tej chwili stoi
  // szkielet, a nie ekran startu organu z serwera).
  // W zamian człowiek nigdy nie widzi strony błędu przeglądarki. Ramki bez
  // sygnału (UCS, moduły partnerskie) pokazują się po `load`, jak dotąd.
  const widoczna = sygnalZycia ? sygnal === 'zyje' : !isLoading;
  const szkielet = sygnalZycia ? sygnal === 'czekam' : isLoading;

  return (
    <div className="relative w-full h-full min-h-screen">
      {szkielet && (
        <div className="absolute inset-0 z-10">
          <ModuleLoadingFallback name={name} />
        </div>
      )}
      {milczy && (
        <div ref={ekranCiszy} className="absolute inset-0 z-10">
          <EkranOrganuNiedostepnego
            organ={nazwaOrganuWZdaniu(moduleId, name)}
            adres={iframeUrl}
            onPonow={() => {
              // Kliknięcie zdejmuje ekran razem z przyciskiem, który ma fokus,
              // więc decyzja „oddaj fokus ramce” zapada tu, nie przy sygnale.
              fokusDoRamki.current = true;
              setSygnal('czekam');
              setProba((p) => p + 1);
            }}
          />
        </div>
      )}
      <iframe
        key={proba}
        ref={iframeRef}
        src={iframeUrl}
        className="w-full h-full min-h-screen border-0"
        // Ukryta ramka ładuje się dalej (`display: none` nie wstrzymuje
        // wczytywania) — spóźniony sygnał pokazuje ją bez ponownego ładowania.
        style={{ display: widoczna ? 'block' : 'none' }}
        onLoad={handleIframeLoad}
        allow={uprawnieniaRamki(bluetooth)}
      />
    </div>
  );
};

// =============================================================================
// BUILT-IN MODULE ROUTE — generic iframe route element, driven by the registry
// (replaces the former 52 per-route wrapper components).
// =============================================================================

export const BuiltInModuleRoute: React.FC<{
  slug: string;
  path?: string;
  /** Cel bez sesji — patrz `ModuleAccessGuard`; domyślnie `/login`. */
  celBezSesji?: string;
}> = ({ slug, path, celBezSesji }) => {
  const def = getModuleDef(slug);
  if (!def || !def.url) {
    return <ModuleDisabledFallback name={def?.displayName ?? slug} moduleId={slug} />;
  }
  return (
    <ModuleAccessGuard moduleId={slug} moduleName={def.displayName} celBezSesji={celBezSesji}>
      <ModuleFrame
        baseUrl={def.url}
        path={path ?? ''}
        name={def.displayName}
        moduleId={slug}
        sygnalZycia={ORGANY_Z_SYGNALEM_ZYCIA.includes(slug)}
        bluetooth={ORGANY_Z_BLUETOOTH.includes(slug)}
      />
    </ModuleAccessGuard>
  );
};

// =============================================================================
// DYNAMIC MODULE FRAME — for partner/marketplace modules loaded from DB
// =============================================================================

interface DynamicModuleFrameProps {
  moduleSlug: string;
  deployUrl: string;
  moduleName: string;
  path?: string;
}

/**
 * Loads any module by its deploy URL from the database.
 * Used for partner-published modules in the marketplace.
 * The module is loaded in an iframe with the same auth passing mechanism
 * as built-in modules.
 *
 * Bez sygnału życia (`sygnalZycia`) — kodu modułu partnera nie mamy, więc nie
 * da się udowodnić, że odezwie się przy montażu (patrz `POZA_SYGNALEM_ZYCIA`).
 */
export const DynamicModuleFrame: React.FC<DynamicModuleFrameProps> = ({
  moduleSlug,
  deployUrl,
  moduleName,
  path = '',
}) => (
  <ModuleAccessGuard moduleId={moduleSlug} moduleName={moduleName}>
    <ModuleFrame baseUrl={deployUrl} path={path} name={moduleName} moduleId={moduleSlug} />
  </ModuleAccessGuard>
);
